import router from '@/router'
import authApi from '@/services/calls/auth'
import { LoginPayload, User } from '@/types/auth'
import { StorageSerializers, useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

interface States {
  token: Ref<string | null>
  user: Ref<User | null>
}

export const useAuthStore = defineStore('auth', () => {
  const states: States = {
    token: useStorage<string | null>('token', null),
    // Note: https://vueuse.org/core/useStorage/#custom-serialization
    // when you provide null as the default value, useStorage can't assume the data type from it.
    // In this case, you can provide a custom serializer or reuse the built-in ones explicitly.
    user: useStorage<User | null>('user', null, undefined, { serializer: StorageSerializers.object }),
  }

  const getUserFullName = computed(() => {
    return `${states.user.value?.user.first_name} ${states.user.value?.user.last_name}`
  })

  const logout = () => {
    states.token.value = null
    router.push({ name: 'Login' })
  }

  const login = async (payload: LoginPayload) => {
    const response = await authApi.login(payload)
    if (response) {
      states.token.value = response.access
      await getUserMe()
    }
  }

  const getUserMe = async () => {
    const response = await authApi.getUserMe()
    states.user.value = response
  }

  const resetStore = () => {
    for (const key in states) {
      states[key as keyof States].value = null
    }
  }

  const resetAllStores = () => {
    const entityStore = useEntityStore()
    const productStore = useProductStore()
    const blacklistedProductsStore = useBlacklistedProductsStore()
    const podFilesStore = usePODFilesStore()
    const preAccountsStore = usePreAccountsStore()
    const reportedIssuesStore = useReportedIssuesStore()
    const barcodeResolutionStore = useBarcodeResolutionStore()
    const miscStore = useMiscStore()

    resetStore()
    entityStore.resetStore()
    productStore.resetStore()
    blacklistedProductsStore.resetStore()
    podFilesStore.resetStore()
    preAccountsStore.resetStore()
    reportedIssuesStore.resetStore()
    barcodeResolutionStore.resetStore()
    miscStore.resetStore()
  }

  return {
    ...states,
    getUserFullName,
    logout,
    login,
    getUserMe,
    resetAllStores,
  }
})
